import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { alpha, Box, Chip, Grid, Popover, Stack, Typography, useTheme } from '@mui/material'
import React from 'react'
import { calculateDealTotals } from '../../utils/dealCalculations'
import { formatDateString } from '../../utils/formatDayjsDateTime'
import { renderColorByString } from '../DataGrid/renderColorByString'
import { formatCurrency } from '../DealsKanban/aggregateStageTotals'

const PRODUCT_COLUMNS = '3fr 2fr 2fr 2fr 1fr'

const isActiveDeal = (deal: any) =>
  deal?.stage === 'Deal Won' && !['Ended', 'Cancelled', 'Replaced'].includes(deal?.dealStatus)

const getDealStatusChip = (deal: any) => {
  if (!deal) return null
  if (isActiveDeal(deal)) return { label: 'Active', color: 'rgb(80, 200, 120)' }
  if (deal?.dealStatus === 'Replaced') return { label: 'Replaced', color: 'rgb(239, 195, 124)' }
  if (deal?.dealStatus === 'Ended') return { label: 'Ended', color: 'rgba(220, 20, 60, 0.9)' }
  if (deal?.dealStatus === 'Cancelled') return { label: 'Cancelled', color: 'rgba(110, 114, 132, 0.5)' }
  if (deal?.stage === 'Deal Won') return { label: 'Won', color: 'rgb(51, 211, 145)' }
  return { label: deal?.dealStatus || deal?.stage || 'In Progress', color: renderColorByString(deal?.stage) }
}

/** Returns a Set of field names that differ between two deals */
const getDiffFields = (dealA: any, dealB: any): Set<string> => {
  if (!dealA || !dealB) return new Set()
  const fields = ['name', 'stage', 'dealStatus', 'startDate', 'endDate', 'type']
  const diffs = new Set<string>()
  for (const field of fields) {
    if (String(dealA?.[field] ?? '') !== String(dealB?.[field] ?? '')) {
      diffs.add(field)
    }
  }
  return diffs
}

/** Build a map of productName -> product for quick diff lookups */
const buildProductMap = (products: any[]) => {
  const map = new Map<string, any>()
  for (const p of products ?? []) {
    if (p?.productName) map.set(p.productName, p)
  }
  return map
}

/** Check if a product field differs from its counterpart in the other deal */
const isProductFieldDiff = (product: any, otherMap: Map<string, any>, field: string) => {
  const other = otherMap.get(product?.productName)
  if (!other) return true // product doesn't exist in the other deal
  return String(product?.[field] ?? '') !== String(other?.[field] ?? '')
}

// ─── DealCard ────────────────────────────────────────────────────────

const DealCard = ({
  deal,
  products,
  label,
  isCurrentlyViewing,
  comparisonDeal,
  comparisonProducts,
}: {
  deal: any
  products: any[]
  label: string
  isCurrentlyViewing: boolean
  comparisonDeal: any
  comparisonProducts: any[]
}) => {
  const theme = useTheme()
  const diffFields = React.useMemo(() => getDiffFields(deal, comparisonDeal), [deal, comparisonDeal])
  const otherProductMap = React.useMemo(() => buildProductMap(comparisonProducts), [comparisonProducts])
  const totals = React.useMemo(() => calculateDealTotals(deal, products ?? []), [deal, products])
  const otherTotals = React.useMemo(() => calculateDealTotals(comparisonDeal, comparisonProducts ?? []), [
    comparisonDeal,
    comparisonProducts,
  ])
  const statusChip = getDealStatusChip(deal)

  const diffBg = alpha(theme.palette.text.highlight ?? '#6bb9f0', 0.19)

  return (
    <Grid
      item
      xs={5}
      sx={{
        bgcolor: 'background.paper',
        backdropFilter: 'blur(7px)',
        border: '1px solid',
        borderColor: isCurrentlyViewing ? 'primary.main' : 'divider',
        borderWidth: isCurrentlyViewing ? 2 : 1,
        p: 2,
        boxShadow: 3,
        position: 'relative',
        borderRadius: 1,
      }}
    >
      {/* Top badges */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600 }}>
          {deal?.type}
          {deal?.semanticVersion && <span style={{ color: theme.palette.text.highlight }}> v{deal.semanticVersion}</span>}
        </Typography>
        {isCurrentlyViewing && (
          <Chip
            label="Currently Viewing"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
      </Box>

      {/* Role label + status chip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontSize: 22, color: renderColorByString(label), fontWeight: 600 }}>{label}</Typography>
        {statusChip && (
          <Chip
            label={statusChip.label}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: `${statusChip.color}22`,
              color: statusChip.color,
              border: '1px solid',
              borderColor: `${statusChip.color}44`,
            }}
          />
        )}
      </Box>

      {/* Deal name */}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          mb: 0.5,
          px: 0.5,
          borderRadius: 0.5,
          ...(diffFields.has('name') && { bgcolor: diffBg }),
        }}
      >
        {deal?.name}
      </Typography>

      {/* Stage – Status */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 0.5,
        }}
      >
        <Typography
          sx={{
            color: renderColorByString(deal?.stage),
            fontWeight: 500,
            fontSize: 14,
            borderRadius: 0.5,
            px: 0.25,
            ...(diffFields.has('stage') && { bgcolor: diffBg }),
          }}
        >
          {deal?.stage}
        </Typography>
        <Typography sx={{ fontSize: 14 }}>–</Typography>
        <Typography
          sx={{
            color: renderColorByString(deal?.dealStatus),
            fontWeight: 500,
            fontSize: 14,
            borderRadius: 0.5,
            px: 0.25,
            ...(diffFields.has('dealStatus') && { bgcolor: diffBg }),
          }}
        >
          {deal?.dealStatus}
        </Typography>
      </Box>

      {/* Dates */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: 13,
            borderRadius: 0.5,
            px: 0.25,
            ...(diffFields.has('startDate') && { bgcolor: diffBg }),
          }}
        >
          {formatDateString(deal?.startDate)}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>–</Typography>
        <Typography
          sx={{
            fontSize: 13,
            borderRadius: 0.5,
            px: 0.25,
            ...(diffFields.has('endDate') && { bgcolor: diffBg }),
          }}
        >
          {formatDateString(deal?.endDate)}
        </Typography>
      </Box>

      {/* Product table */}
      {products?.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {/* Column headers */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: PRODUCT_COLUMNS,
              gap: 1,
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 0.5,
              mb: 0.5,
            }}
          >
            {['Service', 'Status', 'Start', 'End', 'Price'].map(h => (
              <Typography key={h} sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textAlign: 'left' }}>
                {h}
              </Typography>
            ))}
          </Box>

          {/* Product rows */}
          {products.map(product => {
            const isNew = !otherProductMap.has(product?.productName)
            return (
              <Box
                key={product?.id || product?.instanceId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: PRODUCT_COLUMNS,
                  gap: 1,
                  alignItems: 'center',
                  fontSize: 13,
                  py: 0.25,
                  px: 0.5,
                  borderRadius: 0.5,
                  ...(isNew && { bgcolor: diffBg }),
                }}
              >
                <Typography sx={{ textAlign: 'left', fontSize: 13 }} noWrap>
                  {product?.productName}
                </Typography>
                <Typography
                  sx={{
                    color: renderColorByString(product?.status),
                    textAlign: 'left',
                    fontSize: 13,
                    fontWeight: 500,
                    ...(!isNew && isProductFieldDiff(product, otherProductMap, 'status') && { bgcolor: diffBg }),
                    borderRadius: 0.5,
                    px: 0.25,
                  }}
                >
                  {product?.status}
                </Typography>
                <Typography
                  sx={{
                    textAlign: 'left',
                    fontSize: 13,
                    ...(!isNew && isProductFieldDiff(product, otherProductMap, 'startDate') && { bgcolor: diffBg }),
                    borderRadius: 0.5,
                    px: 0.25,
                  }}
                >
                  {formatDateString(product?.startDate)}
                </Typography>
                <Typography
                  sx={{
                    textAlign: 'left',
                    fontSize: 13,
                    ...(!isNew && isProductFieldDiff(product, otherProductMap, 'endDate') && { bgcolor: diffBg }),
                    borderRadius: 0.5,
                    px: 0.25,
                  }}
                >
                  {formatDateString(product?.endDate)}
                </Typography>
                <Typography
                  sx={{
                    textAlign: 'left',
                    fontSize: 13,
                    fontWeight: 500,
                    ...(!isNew && isProductFieldDiff(product, otherProductMap, 'price') && { bgcolor: diffBg }),
                    borderRadius: 0.5,
                    px: 0.25,
                  }}
                >
                  {formatCurrency(product?.price)}
                </Typography>
              </Box>
            )
          })}

          {/* Totals row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: PRODUCT_COLUMNS,
              gap: 1,
              alignItems: 'center',
              borderTop: '1px solid',
              borderColor: 'divider',
              mt: 0.5,
              pt: 0.75,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600, textAlign: 'left' }}>Totals</Typography>
            <Typography sx={{ fontSize: 12, textAlign: 'left', color: 'text.secondary' }}>
              {products.length} service{products.length !== 1 ? 's' : ''}
            </Typography>
            <Box />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                textAlign: 'left',
                ...(totals.monthlyTotal !== otherTotals.monthlyTotal && { color: 'warning.main' }),
              }}
            >
              {formatCurrency(totals.monthlyTotal)}/mo
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                textAlign: 'left',
                ...(totals.total !== otherTotals.total && { color: 'warning.main' }),
              }}
            >
              {formatCurrency(totals.total)}
            </Typography>
          </Box>
        </Box>
      )}
    </Grid>
  )
}

// ─── DealComparisonModal ─────────────────────────────────────────────

const DealComparisonModal = ({
  currentDeal,
  currentDealProducts,
  comparisonDeal,
  comparisonDealProducts,
  currentVersionLabel,
  comparisonVersionLabel,
  open,
  onClose,
  anchorEl,
}: {
  currentDeal: any
  currentDealProducts: any[]
  comparisonDeal: any
  comparisonDealProducts: any[]
  currentVersionLabel: string
  comparisonVersionLabel: string
  open: boolean
  onClose: () => void
  anchorEl: HTMLElement | null
}) => {
  const contextualNote = React.useMemo(() => {
    if (!comparisonDeal || !currentDeal) return ''
    if (comparisonDeal?.dealStatus === 'Ended') {
      return 'The previous version has been ended. This version is now the active agreement.'
    }
    if (isActiveDeal(currentDeal)) {
      return 'This is the current active version, replacing the previous version.'
    }
    return 'Once this version is signed, the previous version will be automatically ended.'
  }, [comparisonDeal?.dealStatus, currentDeal?.stage, currentDeal?.dealStatus])

  if (!comparisonDeal || !currentDeal) return null

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            gap: 2,
            p: 2,
            overflowX: 'auto',
            background: 'none',
            bgcolor: 'transparent',
            backdropFilter: 'blur(7px)',
            borderRadius: 2,
          },
        },
      }}
    >
      <Grid container gap={1} sx={{ justifyContent: 'center' }}>
        {/* Previous version card (left) */}
        <DealCard
          deal={comparisonDeal}
          products={comparisonDealProducts}
          label={comparisonVersionLabel || 'Previous'}
          isCurrentlyViewing={false}
          comparisonDeal={currentDeal}
          comparisonProducts={currentDealProducts}
        />

        {/* Arrow indicator */}
        <Grid
          item
          xs={1}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          <ArrowForwardIcon sx={{ height: 64, width: '100%', color: 'text.secondary', opacity: 0.6 }} />
          <Typography sx={{ fontSize: 10, color: 'text.secondary', textAlign: 'center', mt: 0.5 }}>
            replaced by
          </Typography>
        </Grid>

        {/* Current version card (right) */}
        <DealCard
          deal={currentDeal}
          products={currentDealProducts}
          label={currentVersionLabel || 'Current'}
          isCurrentlyViewing
          comparisonDeal={comparisonDeal}
          comparisonProducts={comparisonDealProducts}
        />

        {/* Contextual notes */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              bgcolor: 'background.paper',
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              boxShadow: 3,
              maxWidth: 560,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{contextualNote}</Typography>
          </Stack>
        </Grid>
      </Grid>
    </Popover>
  )
}

export default DealComparisonModal
